import { handleResponseLanguage } from '../../../../i18n';
import { FormOption } from '../../../../types/Form';
import { useFlowsSelector } from '../../../common/src/utils/globalVariables';
import {
  EnumObject,
  FlowsQuestion,
  isNewEnumFormat,
  isNumberOptions,
  isTextOptions,
} from '../types/FlowsTypes';
import { handleConditions } from '../utils/flowsQuestionnaireUtils';
import { getSortedQuestionOptions } from '../utils/flowsQuestionsUtils';
import useFlowQuestions from './useFlowQuestions';

const useQuestionEnum = (question: FlowsQuestion): FormOption[] => {
  const questions = useFlowQuestions();
  const answers = useFlowsSelector((state) => state.flows.answers);
  const { enum: questionEnum, sort: questionSort } = question.rules;

  /** Prepare the enum before returning */
  const prepareEnum = (enumOptions: EnumObject[]) => {
    let _enumOptions = [...enumOptions];
    _enumOptions = _enumOptions.filter((enumOption) =>
      handleConditions(
        questions,
        answers,
        enumOption.conditions,
        question.index,
      ),
    );

    return _enumOptions;
  };

  /** Prepare the options object array before returning */
  const prepareOptions = (options: FormOption[]) => {
    let _options = [...options];

    _options = getSortedQuestionOptions(_options, questionSort);

    return _options;
  };

  /** If the value is an object, stringify it */
  const prepareValue = (value: string | Record<string, unknown>): string => {
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return value;
  };

  if (isNewEnumFormat(questionEnum)) {
    // NEW ENUM FORMAT
    const preparedEnum = prepareEnum(questionEnum);
    const options = questionEnum.flatMap((option) => {
      const isHidden = !preparedEnum.some(
        ({ value: _value }) => _value === option.value,
      );
      /** When the enum has a disabled_description it should show the enum option but in a disabled state */
      if (!isHidden || option.disabled_description)
        return [
          {
            label: handleResponseLanguage(option.label) || option.value,
            value: prepareValue(option.value),
            icon_key: option.icon_key,
            disabled: isHidden,
            disabled_description: option.disabled_description
              ? handleResponseLanguage(option.disabled_description)
              : undefined,
            description: option.description
              ? handleResponseLanguage(option.description)
              : undefined,
          },
        ];
      return [];
    });

    return prepareOptions(options);
  } else if (isNumberOptions(questionEnum)) {
    // OLD ENUM FORMAT
    return questionEnum.map((num) => ({
      label: num.toString(),
      value: num.toString(),
    }));
  } else if (isTextOptions(questionEnum)) {
    // OLD ENUM FORMAT
    const options =
      (questionEnum || []).map((option) => {
        const [value] = Object.keys(option);
        const label = handleResponseLanguage(option[value]);
        return { label, value };
      }) || [];

    return getSortedQuestionOptions(options, questionSort);
  }

  return [];
};

export default useQuestionEnum;
